import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getAccounts, getPositions, getOrders } from '@/utils/api';
import { TrendUp, TrendDown, Wallet, ChartLine, SignOut, ChartBar, Receipt, Upload, Headset, User, Lock, Crown } from '@phosphor-icons/react';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { formatMoney, formatMetric } from '@/utils/currency';

const ClientLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, positionsRes, ordersRes] = await Promise.all([
        getAccounts(),
        getPositions(),
        getOrders()
      ]);
      if (accountsRes.data.length > 0) {
        setAccount(accountsRes.data[0]);
      }
      setPositions(positionsRes.data);
      setOrders(ordersRes.data.filter(o => o.status === 'PENDING'));
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const navItems = [
    { path: '/dashboard', icon: ChartBar, label: 'Dashboard' },
    { path: '/dashboard/webtrader', icon: ChartLine, label: 'WebTrader' },
    { path: '/dashboard/positions', icon: TrendUp, label: 'Positions' },
    { path: '/dashboard/transactions', icon: Receipt, label: 'Transactions' },
    { path: '/dashboard/kyc', icon: Upload, label: 'Verification' },
    { path: '/dashboard/support', icon: Headset, label: 'Support' },
    { path: '/dashboard/legal-documents', icon: Lock, label: 'Legal Documents' },
    { path: '/dashboard/profile', icon: User, label: 'Profile' },
  ];

  const totalPL = account && 'floating_pnl' in account
    ? account.floating_pnl
    : positions.reduce((sum, pos) => sum + (pos.profit_loss || 0), 0);

  // Account tier derived purely from balance — never a free-form field, so
  // it updates automatically when the client crosses a threshold.
  //   200 ≤ balance < 5,000      → Standard
  //   5,000 ≤ balance < 50,000   → Premium
  //   50,000 ≤ balance < 250,000 → Platinum
  //   balance ≥ 250,000          → VIP
  //   balance < 200              → Standard (default)
  const accountTier = (() => {
    const b = Number(account?.balance || 0);
    if (b >= 250000) return { name: 'VIP', from: 250000, to: null };
    if (b >= 50000)  return { name: 'Platinum', from: 50000, to: 249999 };
    if (b >= 5000)   return { name: 'Premium', from: 5000, to: 49999 };
    return { name: 'Standard', from: 200, to: 4999 };
  })();
  const tierRangeLabel = (() => {
    const ccy = account?.currency || 'USD';
    const fmt = (n) => formatMoney(n, ccy).replace(/\.00$/, '');
    if (accountTier.to == null) return `${fmt(accountTier.from)}+`;
    return `${fmt(accountTier.from)} – ${fmt(accountTier.to)}`;
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loader"></div>
      </div>
    );
  }

  // If we're on a nested route, show the outlet
  if (location.pathname !== '/dashboard') {
    return (
      <div className="min-h-screen bg-background">
        {/* Top Navigation */}
        <nav className="bg-white border-b border-slate-200 px-6 py-3">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-heading font-black text-brand-primary hover:text-brand-accent transition-colors" data-testid="client-brand-link-inner">
              WellingtonCDFS
            </Link>
            <div className="flex items-center gap-6">
              <div className="text-sm">
                <span className="text-slate-600">Balance:</span>
                <span className="font-semibold ml-2 tabular-nums">
                  {formatMoney(account?.balance || 0, account?.currency)}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-slate-600">P/L:</span>
                <span className={`font-semibold ml-2 tabular-nums ${
                  totalPL === null ? 'text-slate-500' : totalPL >= 0 ? 'text-trading-bullish' : 'text-trading-bearish'
                }`}>
                  {formatMetric(totalPL, account?.currency)}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-accent"
              >
                <SignOut size={20} /> Logout
              </button>
            </div>
          </div>
        </nav>

        {/* Side Navigation */}
        <div className="flex">
          <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-60px)]">
            <nav className="p-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-brand-accent text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <item.icon size={20} weight="bold" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  // Dashboard home view
  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" className="text-2xl font-heading font-black text-brand-primary hover:text-brand-accent transition-colors" data-testid="client-brand-link-home">
            WellingtonCDFS
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-sm text-slate-600">Welcome, {user?.full_name}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-accent"
            >
              <SignOut size={20} /> Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Side Navigation */}
      <div className="flex">
        <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-60px)]">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-brand-accent text-white'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <item.icon size={20} weight="bold" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main data-testid={DASHBOARD.container} className="flex-1 p-6">
          {/* Account Tier card — derived automatically from balance. Keeps
              the existing Dashboard card look & feel; no redesign. */}
          <div data-testid="account-tier-card" className="bg-gradient-to-r from-brand-primary to-brand-accent text-white p-6 rounded-xl mb-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
                <Crown size={16} weight="fill" /> Tipo di conto
              </div>
              <div className="text-3xl font-black mt-1" data-testid="account-tier-name">{accountTier.name}</div>
              <div className="text-sm opacity-80 mt-1" data-testid="account-tier-range">{tierRangeLabel}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest opacity-80">Current Balance</div>
              <div className="text-2xl font-bold tabular-nums mt-1">
                {formatMoney(account?.balance || 0, account?.currency)}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div data-testid={DASHBOARD.balanceCard} className="bg-white p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <Wallet size={24} className="text-brand-accent" />
                <span className="text-sm text-slate-600">Balance</span>
              </div>
              <div className="text-3xl font-bold text-brand-primary tabular-nums">
                {formatMoney(account?.balance || 0, account?.currency)}
              </div>
            </div>

            <div data-testid={DASHBOARD.equityCard} className="bg-white p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendUp size={24} className="text-brand-accent" />
                <span className="text-sm text-slate-600">Equity</span>
              </div>
              <div className="text-3xl font-bold text-brand-primary tabular-nums">
                {formatMetric(account?.equity === undefined ? 0 : account.equity, account?.currency)}
              </div>
            </div>

            <div data-testid={DASHBOARD.marginCard} className="bg-white p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <ChartLine size={24} className="text-brand-accent" />
                <span className="text-sm text-slate-600">Free Margin</span>
              </div>
              <div className="text-3xl font-bold text-brand-primary tabular-nums">
                {formatMetric(account?.free_margin === undefined ? 0 : account.free_margin, account?.currency)}
              </div>
            </div>

            <div data-testid={DASHBOARD.plCard} className="bg-white p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                {totalPL >= 0 ? (
                  <TrendUp size={24} className="text-trading-bullish" />
                ) : (
                  <TrendDown size={24} className="text-trading-bearish" />
                )}
                <span className="text-sm text-slate-600">Floating P/L</span>
              </div>
              <div className={`text-3xl font-bold tabular-nums ${
                totalPL === null ? 'text-slate-500' : totalPL >= 0 ? 'text-trading-bullish' : 'text-trading-bearish'
              }`}>
                {formatMetric(totalPL, account?.currency)}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <button
              onClick={() => navigate('/dashboard/webtrader')}
              className="bg-gradient-to-r from-brand-secondary to-brand-accent text-white p-6 rounded-xl font-semibold text-lg hover:shadow-xl transition-shadow"
            >
              Start Trading
            </button>
            <button
              onClick={() => navigate('/dashboard/transactions')}
              className="bg-white border-2 border-brand-secondary text-brand-secondary p-6 rounded-xl font-semibold text-lg hover:bg-brand-secondary hover:text-white transition-colors"
            >
              Deposit Funds
            </button>
            <button
              onClick={() => navigate('/dashboard/kyc')}
              className="bg-white border-2 border-slate-300 text-slate-700 p-6 rounded-xl font-semibold text-lg hover:border-brand-accent hover:text-brand-accent transition-colors"
            >
              Complete KYC
            </button>
          </div>

          {/* Open Positions */}
          <div className="bg-white rounded-xl border border-slate-200 mb-6">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-brand-primary">Open Positions</h2>
            </div>
            <div className="p-6">
              {positions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No open positions</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                        <th className="pb-3">Symbol</th>
                        <th className="pb-3">Direction</th>
                        <th className="pb-3">Volume</th>
                        <th className="pb-3 text-right">Open Price</th>
                        <th className="pb-3 text-right">Current Price</th>
                        <th className="pb-3 text-right">P/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.slice(0, 5).map((position) => (
                        <tr key={position.id} className="border-b border-slate-100">
                          <td className="py-3 font-semibold">{position.symbol}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              position.direction === 'BUY'
                                ? 'bg-trading-bullish-bg text-trading-bullish'
                                : 'bg-trading-bearish-bg text-trading-bearish'
                            }`}>
                              {position.direction}
                            </span>
                          </td>
                          <td className="py-3 tabular-nums">{position.volume}</td>
                          <td className="py-3 text-right tabular-nums">{position.open_price?.toFixed(5)}</td>
                          <td className="py-3 text-right tabular-nums">{position.current_price?.toFixed(5)}</td>
                          <td className={`py-3 text-right font-semibold tabular-nums ${
                            position.profit_loss >= 0 ? 'text-trading-bullish' : 'text-trading-bearish'
                          }`}>
                            {formatMetric(position.profit_loss, account?.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Pending Orders */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-brand-primary">Pending Orders</h2>
            </div>
            <div className="p-6">
              {orders.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No pending orders</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                        <th className="pb-3">Symbol</th>
                        <th className="pb-3">Type</th>
                        <th className="pb-3">Direction</th>
                        <th className="pb-3">Volume</th>
                        <th className="pb-3 text-right">Entry Price</th>
                        <th className="pb-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map((order) => (
                        <tr key={order.id} className="border-b border-slate-100">
                          <td className="py-3 font-semibold">{order.symbol}</td>
                          <td className="py-3">{order.order_type}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              order.direction === 'BUY'
                                ? 'bg-trading-bullish-bg text-trading-bullish'
                                : 'bg-trading-bearish-bg text-trading-bearish'
                            }`}>
                              {order.direction}
                            </span>
                          </td>
                          <td className="py-3 tabular-nums">{order.volume}</td>
                          <td className="py-3 text-right tabular-nums">{order.entry_price?.toFixed(5)}</td>
                          <td className="py-3">
                            <span className="status-badge status-pending">{order.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientLayout;